/* ============================================================================
   Reports — generate a date-range Work Report (a Fable-designed colorful PDF of
   every design worked on in the window, hours per design + total), then keep,
   re-open, and delete the saved PDFs. Open to all authenticated roles.
   ============================================================================ */

import { useCallback, useEffect, useState } from 'react';
import { apiGet, apiPost, apiDelete, isApiError } from '../../lib/api';

type StatusFilter = 'all' | 'completed' | 'in_progress';

interface ReportRun {
  id: number;
  label: string;
  dateFrom: string;
  dateTo: string;
  statusFilter: string;
  totalHours: number;
  designCount: number;
  generatedByName: string | null;
  createdAt: string;
  downloadUrl: string | null;
}

const STATUS_LABEL: Record<string, string> = {
  all: 'All work',
  completed: 'Completed',
  in_progress: 'In progress',
};

function firstOfMonth(): string {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-01`;
}
function today(): string {
  return new Date().toISOString().slice(0, 10);
}

export default function WorkReports() {
  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(today());
  const [status, setStatus] = useState<StatusFilter>('all');

  const [runs, setRuns] = useState<ReportRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await apiGet<ReportRun[]>('/api/reports/runs');
      setRuns(Array.isArray(r) ? r : []);
      setError(null);
    } catch (e) {
      setError(isApiError(e) ? e.message : 'Failed to load saved reports.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function generate(): Promise<void> {
    setGenerating(true);
    setError(null);
    setMsg(null);
    try {
      const run = await apiPost<ReportRun>('/api/reports/generate', { from, to, status });
      setMsg(`Report generated — ${run.designCount} design(s), ${run.totalHours.toFixed(2)} hours.`);
      await load();
      if (run.downloadUrl) window.open(run.downloadUrl, '_blank', 'noreferrer');
    } catch (e) {
      setError(isApiError(e) ? e.message : 'Could not generate the report.');
    } finally {
      setGenerating(false);
    }
  }

  async function remove(run: ReportRun): Promise<void> {
    if (!window.confirm(`Delete "${run.label}"? This cannot be undone.`)) return;
    setBusyId(run.id);
    setError(null);
    try {
      await apiDelete(`/api/reports/runs/${run.id}`);
      await load();
    } catch (e) {
      setError(isApiError(e) ? e.message : 'Could not delete the report.');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="container page">
      <div className="page-title-row">
        <div>
          <p className="eyebrow">Reports</p>
          <h1>Work Report</h1>
        </div>
      </div>

      <div className="card card-pad-lg" style={{ marginBottom: 20 }}>
        <div className="card-header">
          <h3>Generate a report</h3>
        </div>
        <p className="muted" style={{ marginTop: -8 }}>
          Pick a date range — the PDF lists every design worked on in that window (completed and in
          progress), the hours logged per design, and the total.
        </p>
        <div className="row" style={{ flexWrap: 'wrap', gap: 16, alignItems: 'flex-end' }}>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>From</label>
            <input type="date" className="in" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>To</label>
            <input type="date" className="in" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>Include</label>
            <select className="in" value={status} onChange={(e) => setStatus(e.target.value as StatusFilter)}>
              <option value="all">All work</option>
              <option value="completed">Completed only</option>
              <option value="in_progress">In progress only</option>
            </select>
          </div>
          <button className="btn btn-primary" disabled={generating || !from || !to} onClick={() => void generate()}>
            {generating ? 'Generating…' : 'Generate report'}
          </button>
        </div>
        {msg && <p className="meta" style={{ marginTop: 12 }}>{msg}</p>}
        {error && <p className="error-text" style={{ marginTop: 12 }}>{error}</p>}
      </div>

      <div className="card-header">
        <h3>Saved reports</h3>
      </div>

      {loading ? (
        <div className="card" style={{ display: 'grid', placeItems: 'center', padding: 48 }}>
          <div className="spinner" />
        </div>
      ) : runs.length === 0 ? (
        <div className="card empty">
          <h3>No reports yet</h3>
          <p className="muted">Generate your first report above — it will be saved here to re-open or delete.</p>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="tbl tbl-compact">
            <thead>
              <tr>
                <th>Report</th>
                <th>Range</th>
                <th>Include</th>
                <th style={{ textAlign: 'right' }}>Designs</th>
                <th style={{ textAlign: 'right' }}>Hours</th>
                <th>Generated by</th>
                <th>Created</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id}>
                  <td style={{ fontWeight: 600 }}>
                    {r.downloadUrl ? (
                      <a href={r.downloadUrl} target="_blank" rel="noreferrer">
                        {r.label}
                      </a>
                    ) : (
                      r.label
                    )}
                  </td>
                  <td className="mono">
                    {r.dateFrom} → {r.dateTo}
                  </td>
                  <td>{STATUS_LABEL[r.statusFilter] ?? r.statusFilter}</td>
                  <td className="mono" style={{ textAlign: 'right' }}>
                    {r.designCount}
                  </td>
                  <td className="mono" style={{ textAlign: 'right' }}>
                    {r.totalHours.toFixed(2)}
                  </td>
                  <td>{r.generatedByName ?? '—'}</td>
                  <td className="mono">{r.createdAt.slice(0, 16).replace('T', ' ')}</td>
                  <td style={{ textAlign: 'right' }}>
                    <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                      {r.downloadUrl && (
                        <a className="btn btn-sm btn-ghost" href={r.downloadUrl} target="_blank" rel="noreferrer">
                          View
                        </a>
                      )}
                      <button
                        className="btn btn-sm btn-ghost"
                        style={{ color: '#b3261e' }}
                        disabled={busyId === r.id}
                        onClick={() => void remove(r)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
